/**
 * Concurrency Limiter Utility
 * Limits the number of concurrent async operations
 */

/**
 * Sleep for specified milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Generate jittered delay value
 * @param base - Base delay in milliseconds
 * @param spread - Random spread in milliseconds (0 to spread)
 * @returns base + random(0, spread)
 */
export function jitter(base: number, spread: number): number {
  return base + Math.floor(Math.random() * spread);
}

/**
 * Concurrency limiter
 * Limits the number of concurrent async operations
 * Uses a semaphore pattern with FIFO queue
 */
export function createLimiter(concurrency: number): {
  run<T>(fn: () => Promise<T>): Promise<T>;
} {
  if (concurrency < 1) {
    throw new Error("Concurrency must be at least 1");
  }

  let active = 0;
  const queue: Array<() => void> = [];

  const processNext = (): void => {
    if (active >= concurrency || queue.length === 0) {
      return;
    }

    const next = queue.shift();
    if (next) {
      active++;
      next();
    }
  };

  return {
    async run<T>(fn: () => Promise<T>): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        const execute = async (): Promise<void> => {
          try {
            const result = await fn();
            active--;
            resolve(result);
            processNext();
          } catch (error) {
            active--;
            reject(error);
            processNext();
          }
        };

        if (active < concurrency) {
          active++;
          void execute();
        } else {
          queue.push(execute);
        }
      });
    },
  };
}
